class SolanaDistributionWebSocket {
  constructor() {
    this.ws = null;
    this.connectionPromise = null;
    this.callbacks = new Map();
    this.retryCount = 0;
    this.maxRetries = 5;
    this.retryDelay = 1000; // Start with 1 second
    this.isManuallyDisconnected = false;
  }

  async connect(retryCount = 0) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      return this.ws;
    }

    if (this.connectionPromise) {
      return this.connectionPromise;
    }

    this.connectionPromise = new Promise((resolve, reject) => {
      const token = localStorage.getItem('token');
      if (!token) {
        reject(new Error('No authentication token found'));
        return;
      }

      // Determine the correct WebSocket URL based on environment
      let wsUrl;
      if (process.env.NODE_ENV === 'production') {
        // For Heroku deployment, use the current host with wss protocol
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        wsUrl = `${protocol}//${window.location.host}/ws/solana-distribution?token=${token}`;
      } else {
        // For local development, use localhost with the backend port
        const backendPort = process.env.REACT_APP_BACKEND_PORT || '5001';
        wsUrl = `ws://localhost:${backendPort}/ws/solana-distribution?token=${token}`;
      }

      console.log('Connecting to Solana distribution WebSocket:', wsUrl);
      console.log('Token (first 20 chars):', token ? token.substring(0, 20) + '...' : 'NO TOKEN');
      console.log('Environment:', process.env.NODE_ENV);
      console.log('Retry count:', retryCount);

      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        console.log('Solana distribution WebSocket connected successfully');
        this.connectionPromise = null;
        this.retryCount = 0; // Reset retry count on successful connection
        this.isManuallyDisconnected = false; // Reset manual disconnect flag
        resolve(this.ws);
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          this.handleMessage(data);
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error);
        }
      };

      this.ws.onclose = (event) => {
        console.log('Solana distribution WebSocket disconnected:', event.code, event.reason);
        console.log('Close event details:', { code: event.code, reason: event.reason, wasClean: event.wasClean });
        this.ws = null;
        this.connectionPromise = null;
        
        // Notify all callbacks about disconnection
        this.callbacks.forEach(callback => {
          callback({ type: 'disconnected', reason: event.reason, code: event.code });
        });

        // Auto-reconnect logic for unexpected disconnections
        if (!event.wasClean && event.code !== 1000) {
          this.attemptReconnect();
        }
      };

      this.ws.onerror = (error) => {
        console.error('Solana distribution WebSocket error:', error);
        console.log('WebSocket readyState when error occurred:', this.ws ? this.ws.readyState : 'no ws');
        this.connectionPromise = null;
        
        // Don't reject immediately - let reconnection logic handle it
        if (retryCount === 0) {
          this.attemptReconnect();
        }
        reject(error);
      };

      // Set timeout for connection
      setTimeout(() => {
        if (this.ws && this.ws.readyState !== WebSocket.OPEN) {
          this.ws.close();
          reject(new Error('WebSocket connection timeout'));
        }
      }, 10000);
    });

    return this.connectionPromise;
  }

  handleMessage(data) {
    console.log('Received WebSocket message:', data);
    
    // Notify all registered callbacks
    this.callbacks.forEach(callback => {
      try {
        callback(data);
      } catch (error) {
        console.error('Error in WebSocket callback:', error);
      }
    });
  }

  // Register callback for messages
  onMessage(callbackId, callback) {
    this.callbacks.set(callbackId, callback);
  }

  // Unregister callback
  offMessage(callbackId) {
    this.callbacks.delete(callbackId);
  }

  // Subscribe to messages (alias for onMessage)
  subscribe(callbackId, callback) {
    this.onMessage(callbackId, callback);
  }

  // Unsubscribe from messages (alias for offMessage)
  unsubscribe(callbackId) {
    this.offMessage(callbackId);
  }

  async send(data) {
    const ws = await this.connect();
    if (ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(data));
    } else {
      throw new Error('WebSocket is not connected');
    }
  }

  async distribute(sourceWalletId, destinations) {
    await this.send({
      type: 'distribute',
      sourceWalletId,
      destinations
    });
  }

  async gather(destinationWalletId, sources, gatherAll = false) {
    await this.send({
      type: 'gather',
      destinationWalletId,
      sources,
      gatherAll
    });
  }

  async ping() {
    await this.send({ type: 'ping' });
  }

  attemptReconnect() {
    if (this.isManuallyDisconnected || this.retryCount >= this.maxRetries) {
      console.log('Not attempting reconnect:', { 
        isManuallyDisconnected: this.isManuallyDisconnected, 
        retryCount: this.retryCount, 
        maxRetries: this.maxRetries 
      });
      return;
    }

    this.retryCount++;
    const delay = Math.min(this.retryDelay * Math.pow(2, this.retryCount - 1), 30000); // Max 30 seconds
    
    console.log(`Attempting WebSocket reconnection in ${delay}ms (attempt ${this.retryCount}/${this.maxRetries})`);
    
    // Notify callbacks about reconnection attempt
    this.callbacks.forEach(callback => {
      callback({ 
        type: 'reconnecting', 
        attempt: this.retryCount, 
        maxRetries: this.maxRetries,
        delay 
      });
    });

    setTimeout(() => {
      console.log(`Reconnection attempt ${this.retryCount}/${this.maxRetries}`);
      this.connect(this.retryCount).catch(error => {
        console.error(`Reconnection attempt ${this.retryCount} failed:`, error);
        if (this.retryCount < this.maxRetries) {
          this.attemptReconnect();
        } else {
          console.log('Max reconnection attempts reached');
          this.callbacks.forEach(callback => {
            callback({ 
              type: 'reconnection_failed', 
              message: 'Max reconnection attempts reached' 
            });
          });
        }
      });
    }, delay);
  }

  disconnect() {
    this.isManuallyDisconnected = true;
    if (this.ws) {
      this.ws.close(1000, 'Manual disconnect'); // Use normal closure code
      this.ws = null;
    }
    this.connectionPromise = null;
    this.callbacks.clear();
    this.retryCount = 0;
  }
}

// Create singleton instance
const solanaDistributionWS = new SolanaDistributionWebSocket();

export default solanaDistributionWS;